import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Button,
  Pagination,
  CircularProgress,
  Avatar,
  Dialog,
  DialogContent,
  IconButton,
  Fade,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { QK } from "../../../base/qk";
import {
  deletePhotograph,
  getAllPhotographs,
} from "../../../api/services/photographs";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import PhotographsModal from "./PhotographsModal";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import DataTable from "../../Table/DataTable";

const Photographs = () => {
  const { id: ClientId } = useParams();
  const [showPhotographsModal, setShowPhotographsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPhotograph, setSelectedPhotograph] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const photographsPerPage = 10;
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery(
    [QK.photographData, ClientId, currentPage],
    () => getAllPhotographs(ClientId, currentPage),
    {
      keepPreviousData: true,
      enabled: !!ClientId,
    }
  );

  const deleteMutation = useMutation(deletePhotograph, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.photographData, ClientId]);
      enqueueSnackbar("Photograph deleted successfully", { variant: "success" });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete photograph", { variant: "error" });
    },
  });

  const handleDelete = (photographId) => {
    setSelectedPhotograph(photographId);
    setShowConfirmModal(true);
  };

  const handleClose = () => {
    setShowConfirmModal(false);
    setSelectedPhotograph(null);
  };

  const handleConfirm = () => {
    if (selectedPhotograph) {
      deleteMutation.mutate(selectedPhotograph);
    }
  };

  const handlePhotographsShow = () => setShowPhotographsModal(true);
  const handlePhotographsClose = () => setShowPhotographsModal(false);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageViewer(true);
  };

  const handleImageViewerClose = () => {
    setShowImageViewer(false);
    setSelectedImage(null);
  };

  const totalRecords = data?.count || 0;
  const totalPages = Math.ceil(totalRecords / photographsPerPage);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const headers = [
    { key: 'image', label: 'Image' },
    { key: 'caption', label: 'Caption' },
    { key: 'uploaded_by_name', label: 'Uploaded By' },
    { key: 'created_at', label: 'Uploaded At' },
  ];

  const renderCell = (item, header) => {
    const key = typeof header === 'object' ? header.key : header;
    
    if (key === 'image') {
      return (
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            cursor: 'pointer',
            '&:hover .zoom-icon': {
              opacity: 1,
            },
          }}
          onClick={() => handleImageClick(item.image)}
        >
          <Avatar
            src={item.image}
            alt={item.caption || 'Photograph'}
            variant="rounded"
            sx={{
              width: 50,
              height: 50,
              borderRadius: "8px",
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
            }}
          >
            <ImageIcon />
          </Avatar>
          <Box
            className="zoom-icon"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.4)',
              borderRadius: '8px',
              opacity: 0,
              transition: 'opacity 0.2s ease',
            }}
          >
            <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
        </Box>
      );
    }
    
    if (key === 'caption') {
      return item.caption || 'No caption';
    }
    
    if (key === 'uploaded_by_name') {
      return item.uploaded_by_name || 'Unknown';
    }
    
    if (key === 'created_at') {
      if (!item.created_at) return '-';
      const date = new Date(item.created_at);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    
    return item[key] || '-';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="error">Error fetching photographs.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Card
        sx={{
          borderRadius: "16px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <CardHeader
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: "1px solid #F2F4F7",
          }}
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    bgcolor: "#EFF8FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PhotoCameraIcon sx={{ color: "#2469F0", fontSize: 22 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                  Photographs Gallery
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "#F2F4F7",
                    px: 1,
                    py: 0.5,
                    borderRadius: "20px",
                    color: "#667085",
                    fontWeight: 500,
                  }}
                >
                  {totalRecords}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handlePhotographsShow}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  bgcolor: "#2469F0",
                  "&:hover": {
                    bgcolor: "#1A5BD4",
                    boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                  },
                }}
              >
                Add Photograph
              </Button>
            </Box>
          }
        />

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={data?.results || []}
            headers={headers}
            onDelete={handleDelete}
            canDelete={true}
            loading={isLoading}
            renderCell={renderCell}
          />

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Image Viewer Dialog */}
      <Dialog
        open={showImageViewer}
        onClose={handleImageViewerClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            borderRadius: '12px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            position: 'relative',
          },
        }}
      >
        <IconButton
          onClick={handleImageViewerClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 10,
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.7)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Photograph"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '4px',
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <PhotographsModal
        show={showPhotographsModal}
        onHide={handlePhotographsClose}
        refetch={refetch}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        title="Delete Photograph"
        body="Are you sure you want to delete this photograph? This action cannot be undone."
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default Photographs;